import { Component, OnInit } from "@angular/core";
import { UserMgmtService } from "../user.service";
import { Router, ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { ExcelService } from "../../../shared/excel.service";
import { DashboardService } from "../../dashboard/dashboard.service";

@Component({
  selector: "app-user-source",
  templateUrl: "./user-source.component.html",
  styleUrls: ["./user-source.component.scss"],
})
export class UserSourceComponent implements OnInit {
  userList: any[] = [];
  total: number = 0;
  pageLimit: number = 10;
  currentPage: number = 0;
  totalPage: number = 1;
  isLoading: boolean = false;
  searchStartDate: any;
  searchEndDate: any;
  minDate: any = new Date(2022, 0, 1);
  maxDate: any = new Date(2050, 11, 31);
  selectedTab: string = "all";
  searchText: string = "";
  private searchSubject: Subject<string> = new Subject<string>();

  userCounts: any;

  constructor(
    private userMgmtService: UserMgmtService,
    private router: Router,
    private route: ActivatedRoute,
    private excelService: ExcelService,
    private dashboardService: DashboardService
  ) {}

  ngOnInit() {
    this.getUserCountData();

    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 0;
        this.updateQueryParams();
      });

    this.route.queryParams.subscribe((params) => {
      if (Object.keys(params).length === 0) {
        this.applyTabFilter("all", true);
      } else {
        this.selectedTab = params["tab"] || "all";
        this.searchText = params["text"] || "";
        this.currentPage = params["page"] ? +params["page"] : 0;
        this.pageLimit = params["limit"] ? +params["limit"] : 10;

        if (params["startDate"]) {
          const parts = params["startDate"].split("-");
          this.searchStartDate = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
          );
        } else {
          this.searchStartDate = null;
        }

        if (params["endDate"]) {
          const parts = params["endDate"].split("-");
          this.searchEndDate = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
          );
        } else {
          this.searchEndDate = null;
        }

        this.fetchUserDetails(true);
      }
    });
  }

  getUserCountData() {
    this.dashboardService.getUserCountsApi().subscribe(
      (res) => {
        if (res["statusCode"] === 200) {
          this.userCounts = res["data"];
        }
      },
      (err) => {
        console.error("Error fetching user counts", err);
      }
    );
  }

  updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: this.selectedTab,
        startDate: this.searchStartDate ? this.formatDate(this.searchStartDate) : null,
        endDate: this.searchEndDate ? this.formatDate(this.searchEndDate) : null,
        text: this.searchText || null,
        page: this.currentPage > 0 ? this.currentPage : null,
        limit: this.pageLimit !== 10 ? this.pageLimit : null,
      },
      queryParamsHandling: "merge",
    });
  }

  applyTabFilter(filter: string, updateUrl: boolean = true) {
    this.selectedTab = filter;
    const now = new Date();
    this.searchEndDate = now;

    if (filter === "lastWeek") {
      const lastWeek = new Date();
      lastWeek.setDate(now.getDate() - 7);
      this.searchStartDate = lastWeek;
    } else if (filter === "lastMonth") {
      const lastMonth = new Date();
      lastMonth.setMonth(now.getMonth() - 1);
      this.searchStartDate = lastMonth;
    } else if (filter === "last3Month") {
      const last3Month = new Date();
      last3Month.setMonth(now.getMonth() - 3);
      this.searchStartDate = last3Month;
    } else if (filter === "last6Month") {
      const last6Month = new Date();
      last6Month.setMonth(now.getMonth() - 6);
      this.searchStartDate = last6Month;
    } else if (filter === "all") {
      this.searchStartDate = null;
      this.searchEndDate = null;
    } else if (filter === "custom") {
      this.minDate = new Date(2022, 0, 1);
      this.maxDate = new Date(2050, 11, 31);
      if (updateUrl) {
        this.updateQueryParams();
      }
      return;
    }

    this.currentPage = 0;
    if (updateUrl) {
      this.updateQueryParams();
    }
  }

  viewDetail(userId: string) {
    if (userId) {
      this.router.navigate(["/main/user-management/user-analytics-detail", userId], {
        queryParamsHandling: "preserve",
      });
    }
  }

  formatDate(date: any): string {
    if (!date) return "";
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  fetchUserDetails(showLoader: boolean = true) {
    if (showLoader) {
      this.isLoading = true;
    }
    const offset = this.currentPage * this.pageLimit;

    let query = `offset=${offset}&limit=${this.pageLimit}`;

    if (this.searchStartDate) {
      query += `&lastBookedFrom=${this.formatDate(this.searchStartDate)}`;
    }
    if (this.searchEndDate) {
      query += `&lastBookedTo=${this.formatDate(this.searchEndDate)}`;
    }
    if (this.searchText) {
      query += `&text=${this.searchText.trim()}`;
    }

    this.userMgmtService.getUserSourceApi(query).subscribe(
      (res) => {
        this.isLoading = false;
        if (res["statusCode"] === 200) {
          const data = res["data"] || {};
          this.userList = data["userList"] || data["sourceList"] || data["list"] || (Array.isArray(data) ? data : []);
          this.total = data["totalCount"] || data["total"] || this.userList.length || 0;
          this.setPagination();
        } else {
          this.userList = [];
          this.total = 0;
        }
      },
      (err) => {
        this.isLoading = false;
        this.userList = [];
        this.total = 0;
      }
    );
  }

  getUserSourceDisplay(row: any): string {
    if (!row) return "--";
    return (
      row.hearAboutUs ||
      row.userSource ||
      row.user_source ||
      row.source ||
      row.registeredSource ||
      row.signupSource ||
      (row.metaData && row.metaData.source) ||
      "--"
    );
  }

  setPagination() {
    this.totalPage = Math.ceil(this.total / this.pageLimit);
    if (this.totalPage === 0) this.totalPage = 1;
  }

  nextPage() {
    if (this.currentPage + 1 < this.totalPage) {
      this.currentPage++;
      this.updateQueryParams();
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateQueryParams();
    }
  }

  changePageLimit() {
    this.currentPage = 0;
    this.updateQueryParams();
  }

  searchFiltersBtn() {
    this.currentPage = 0;
    this.updateQueryParams();
  }

  onReset() {
    this.searchText = "";
    this.applyTabFilter("all");
  }

  onSearchChange() {
    this.searchSubject.next(this.searchText);
  }

  onExport() {
    this.isLoading = true;
    let query = `fetchAll=true`;

    if (this.searchStartDate) {
      query += `&lastBookedFrom=${this.formatDate(this.searchStartDate)}`;
    }
    if (this.searchEndDate) {
      query += `&lastBookedTo=${this.formatDate(this.searchEndDate)}`;
    }
    if (this.searchText) {
      query += `&text=${this.searchText.trim()}`;
    }

    this.userMgmtService.getUserSourceApi(query).subscribe(
      (res) => {
        this.isLoading = false;
        if (res["statusCode"] === 200) {
          const data = res["data"] || {};
          const list = data["userList"] || data["sourceList"] || data["list"] || (Array.isArray(data) ? data : []);
          if (list.length > 0) {
            const mappedList = list.map((item) => ({
              ...item,
              userSource: this.getUserSourceDisplay(item),
            }));
            this.excelService.generateExcel(mappedList);
          }
        }
      },
      (err) => {
        this.isLoading = false;
      }
    );
  }
}
